import { RSAA } from 'redux-api-middleware'
import { path, either, pathOr, pipe, pick, prop } from 'ramda'
import {
  getApiPath,
  mapObjectKeys,
  makeQueryParams,
} from 'sidecar-utility-library'
import { makePaginationParams } from 'sidecar-utilities'

import { initialState, types } from 'store/Doctor'
import { getCurrentPage } from 'utils/interface'

export const fetchDoctors = () => ({
  [RSAA]: {
    endpoint: ({ Doctor = {}, SearchFilters = {} }) => {
      const page = getCurrentPage(Doctor.searchResults.page)
      const size = Doctor.searchResults.size
      const searchFilters = SearchFilters?.filters

      // these will be given searchFilters in the pipe below
      const lat = path(['searchLocation', 'lat'])
      const lon = path(['searchLocation', 'lon'])
      const city = path(['searchLocation', 'city'])
      //TODO: This fixes the hard crash of the Providers page, but there are other conflicts between the fact that we use the term state internally, and the term region from google maps.
      const state = either(
        path(['searchLocation', 'state']),
        path(['searchLocation', 'region']),
      )
      const radius = pathOr(8, ['searchLocation', 'radius'])

      const queryParams = pipe(
        mapObjectKeys({
          page,
          size,
          lat,
          lon,
          city,
          state,
          radius,
          query: either(path(['careSearchQuery']), path(['searchQuery'])),
          type: path(['type']),
          specialty: path(['specialty']),
          gender: path(['gender']),
          language: prop('language'),
          priceFairness: path(['priceFairness']),
          firstName: path(['firstName']),
          lastName: path(['lastName']),
          // sessionId is for search results monitoring.
          // We use the sessionId to link our monitoring data to the heap session.
          // This is an optional param on the request
          // intentCallId is similar, it links our smart layer searches with
          // our tracking data.
          sessionId: window?.heap?.getSessionId?.() || '',
          intentCallId: searchFilters?.intentCallId || '',
        }),
        pick([
          'page',
          'size',
          'lat',
          'lon',
          'city',
          'state',
          'radius',
          'query',
          'type',
          'specialty',
          'gender',
          'language',
          'priceFairness',
          'firstName',
          'lastName',
          'sessionId',
          'intentCallId',
        ]),
        makeQueryParams,
      )(searchFilters)
      return getApiPath('doc', `doctors${queryParams}`, 'v2')
    },
    method: 'GET',
    types: [
      types.FETCH_DOCTORS_REQUEST,
      types.FETCH_DOCTORS_SUCCESS,
      types.FETCH_DOCTORS_ERROR,
    ],
    cache: false,
  },
})

export const fetchSearchSuggestions = query => ({
  [RSAA]: {
    endpoint: ({ Location, SearchFilters }) => {
      const currentLocation = Location.currentLocation.entity
      const mapLocation = SearchFilters?.filters?.searchLocation
      const { lat, lon } =
        mapLocation.lat && mapLocation.lon ? mapLocation : currentLocation
      const searchFilters = SearchFilters?.filters
      const params = {
        // `query` is coming in from the loadOptions function
        // for the autocomplete. Filters in redux used as backup.
        // Use a space as the final backup because makeQueryParams
        // will strip out `query` if it's just an empty string and
        // API will throw 400 because query is a required param.
        query: query || searchFilters.query || ' ',
        lat,
        lon,
        // because suggestions should be pretty broad, chose 100 miles as a baseline
        radius: 100,
      }
      return getApiPath(
        'doc',
        `doctors/suggest${makeQueryParams(params)}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      types.FETCH_SUGGESTIONS_REQUEST,
      types.FETCH_SUGGESTIONS_SUCCESS,
      types.FETCH_SUGGESTIONS_ERROR,
    ],
  },
})

/**
 * Simplified version of fetchDoctors that accepts parameters directly instead
 * of pulling them from Redux state
 *
 * @param {Object} params - Search parameters
 * @param {string} [params.query] - Search query term
 * @param {string} [params.specialty] - Doctor specialty
 * @param {string} [params.type] - Provider type
 * @param {number} [params.lat] - Latitude
 * @param {number} [params.lon] - Longitude
 * @param {string} [params.zip] - ZIP code
 * @param {string} [params.city] - City name
 * @param {string} [params.state] - State code
 * @param {number} [params.radius=8] - Search radius. Default is `8`
 * @param {string} [params.gender] - Provider gender
 * @param {string} [params.language] - Provider language
 * @param {string} [params.priceFairness] - Price fairness rating
 * @param {number} [params.page=0] - Page number. Default is `0`
 * @param {number} [params.size=25] - Page size. Default is `25`
 * @returns {Object} Redux API Middleware action
 */
export const fetchSimpleDoctors = (params = {}) => {
  return {
    [RSAA]: {
      endpoint: ({ Doctor: { searchResults = {} } }) => {
        return getApiPath(
          'doc',
          `doctors${makeQueryParams({
            sessionId: window?.heap?.getSessionId?.(),
            ...params,
            ...makePaginationParams(searchResults),
          })}`,
          'v2',
        )
      },
      method: 'GET',
      types: [
        types.FETCH_DOCTORS_REQUEST,
        types.FETCH_DOCTORS_SUCCESS,
        types.FETCH_DOCTORS_ERROR,
      ],
    },
  }
}

export const resetDoctorSearch = () => ({
  type: types.RESET_DOCTOR_SEARCH,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.FETCH_DOCTORS_REQUEST: {
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: true,
          error: null,
        },
      }
    }
    case types.FETCH_DOCTORS_SUCCESS: {
      const { content = [], page, last } = payload
      const isLastPageReached = last || content.length === 0
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: false,
          page,
          isLastPageReached,
          entities: [...state.searchResults.entities, ...content],
        },
      }
    }
    case types.FETCH_DOCTORS_ERROR: {
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: false,
          error: payload.response.message,
        },
      }
    }

    // ----------

    case types.RESET_DOCTOR_SEARCH: {
      // should clear everything in search results: page, last, entities, et al.
      return {
        ...state,
        searchResults: initialState.searchResults,
      }
    }

    // ----------

    default:
      return state
  }
}
